import { Component, OnInit, ViewChild, ElementRef, Input } from '@angular/core';
import { ModalService } from 'src/app/services/modal/modal.service';
import * as XLSX from 'xlsx';
import { ModalFormUsersComponent } from 'src/app/components/ui/modal-form-users/modal-form-users.component';


export interface User {
  id: string;
  name: string;
  email: string; // Ya no es opcional
  phone?: string;
  grupo?: string;
  expiredDate?: Date;
  active: boolean;
  createdAt: Date;
  usageTime: number;
}

@Component({
    selector: 'app-user-management',
    // standalone: true,
    // imports: [],
    templateUrl: './user-management.component.html',
    styleUrls: ['./user-management.component.scss'],
    standalone: false
})
export class UserManagementComponent implements OnInit {
  @Input() allSettings: boolean = false;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  users: User[] = [];
  filteredUsers: User[] = [];
  searchTerm: string = '';

  showModal: boolean = false;
  editingUser: boolean = false;
  currentUser: User = this.getEmptyUser();

  successMessage: string = '';
  errorMessage: string = '';

  constructor(private modalService: ModalService) {}

  ngOnInit(): void {
    this.loadMockData();
  }

  // Cargar datos de ejemplo
  loadMockData(): void {
    this.users = [
      {
        id: 'U001',
        name: 'Juan Pérez García',
        email: 'juan.perez@email.com',
        phone: '+34 600 111 222',
        active: true,
        createdAt: new Date('2024-01-15'),
        usageTime: 1850 // ~30h 50min
      },
      {
        id: 'U002',
        name: 'María García López',
        email: 'maria.garcia@email.com',
        phone: '+34 600 333 444',
        active: true,
        createdAt: new Date('2024-02-20'),
        usageTime: 2340 // ~39h
      },
      {
        id: 'U003',
        name: 'Carlos López Martín',
        email: 'carlos.lopez@email.com',
        phone: '+34 600 555 666',
        active: false,
        createdAt: new Date('2024-03-10'),
        usageTime: 980 // ~16h 20min
      },
      {
        id: 'U004',
        name: 'Ana Martínez Ruiz',
        email: 'ana.martinez@email.com',
        phone: '+34 600 777 888',
        active: true,
        createdAt: new Date('2024-04-05'),
        usageTime: 1560 // ~26h
      },
      {
        id: 'U005',
        name: 'Luis Rodríguez Sánchez',
        email: 'luis.rodriguez@email.com',
        phone: '+34 600 999 000',
        active: true,
        createdAt: new Date('2024-05-12'),
        usageTime: 3200 // ~53h 20min
      }
    ];
    this.filteredUsers = [...this.users];
  }

  // Estadísticas
  getActiveUsers(): number {
    return this.users.filter(u => u.active).length;
  }

  getInactiveUsers(): number {
    return this.users.filter(u => !u.active).length;
  }

  // Filtrado
  filterUsers(): void {
    const term = this.searchTerm.toLowerCase();
    this.filteredUsers = this.users.filter(user =>
      user.id.toLowerCase().includes(term) ||
      user.name.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      user.phone?.includes(term)
    );
  }

  // Modal
  openAddUserModal(): void {
    const modalRef = this.modalService.open(ModalFormUsersComponent, {
      centered: true,
      size: 'md',
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.user = this.getEmptyUser();
    modalRef.componentInstance.editing = false;
  }

  //   openAddUserModal(): void {
  //   this.editingUser = false;
  //   this.currentUser = this.getEmptyUser();
  //   this.showModal = true;
  // }

  editUser(user: User): void {
    this.editingUser = true;
    this.currentUser = { ...user };
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.currentUser = this.getEmptyUser();
  }

  saveUser(): void {
    // Validaciones
    if (!this.currentUser.id || !this.currentUser.name || !this.currentUser.email) {
      this.errorMessage = 'ID, Nombre y Email son obligatorios';
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.currentUser.email)) {
      this.errorMessage = 'El formato del email no es válido';
      return;
    }

    if (this.editingUser) {
      // Actualizar usuario existente
      const index = this.users.findIndex(u => u.id === this.currentUser.id);
      if (index !== -1) {
        this.users[index] = { ...this.currentUser };
        this.successMessage = 'Usuario actualizado correctamente';
      }
    } else {
      // Verificar ID duplicado
      if (this.users.some(u => u.id === this.currentUser.id)) {
        this.errorMessage = 'Ya existe un usuario con ese ID';
        return;
      }

      // Verificar email duplicado
      if (this.users.some(u => u.email === this.currentUser.email)) {
        this.errorMessage = 'Ya existe un usuario con ese email';
        return;
      }

      // Añadir nuevo usuario
      this.users.push({ ...this.currentUser, createdAt: new Date() });
      this.successMessage = 'Usuario añadido correctamente';
    }

    this.filteredUsers = [...this.users];
    this.closeModal();
    this.clearMessages();
  }

  // Acciones sobre usuarios
  toggleUserStatus(user: User): void {
    user.active = !user.active;
    this.successMessage = `Usuario ${user.active ? 'activado' : 'desactivado'} correctamente`;
    this.clearMessages();
  }

  deleteUser(user: User): void {
    if (confirm(`¿Estás seguro de eliminar al usuario ${user.name}?`)) {
      this.users = this.users.filter(u => u.id !== user.id);
      this.filteredUsers = [...this.users];
      this.successMessage = 'Usuario eliminado correctamente';
      this.clearMessages();
    }
  }

  deleteAllUsers(): void {
    if (confirm(`¿Estás seguro de eliminar TODOS los usuarios (${this.users.length})?`)) {
      this.users = [];
      this.filteredUsers = [];
      this.successMessage = 'Todos los usuarios han sido eliminados';
      this.clearMessages();
    }
  }

  // Carga de fichero
  triggerFileUpload(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.processFile(file);
    }
  }

  async processFile(file: File): Promise<void> {
    try {
      const workbook = XLSX.read(await file.arrayBuffer());
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const data: any[] = XLSX.utils.sheet_to_json(worksheet);

      let added = 0;
      let updated = 0;

      data.forEach((row: any) => {
        if (row.id && row.name) {
          const user: User = {
            id: row.id.toString(),
            name: row.name,
            email: row.email,
            phone: row.phone,
            grupo: row.grupo,
            expiredDate: row.expiredDate ? new Date(row.expiredDate) : undefined,
            active: row.active === undefined ? true : !!row.active,
            createdAt: new Date(),
            usageTime: row.usageTime || 0
          };

          const existingIndex = this.users.findIndex(u => u.id === user.id);
          if (existingIndex !== -1) {
            this.users[existingIndex] = user;
            updated++;
          } else {
            this.users.push(user);
            added++;
          }
        }
      });

      this.filteredUsers = [...this.users];
      this.successMessage = `Fichero procesado: ${added} usuarios añadidos, ${updated} actualizados`;
      this.clearMessages();
    } catch (error) {
      this.errorMessage = 'Error al procesar el fichero';
      console.error(error);
      this.clearMessages();
    }

    // Limpiar input
    this.fileInput.nativeElement.value = '';
  }

  // Descargar plantilla Excel
  downloadTemplate(): void {
    var template = this.users.map(user => ({
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      grupo: user.grupo || '',
      expiredDate: user.expiredDate ? user.expiredDate.toISOString().split('T')[0] : '',
      active: user.active,
      usageTime: user.usageTime
    }));

    const worksheet = XLSX.utils.json_to_sheet(template);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios');
    XLSX.writeFile(workbook, 'plantilla_usuarios.xlsx');
  }

  // Método para formatear tiempo de uso
  formatUsageTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${mins}m`;
  }

  // Método para obtener la fecha de hoy (para el campo date)
  getTodayDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Utilidades
  private getEmptyUser(): User {
    return {
      id: '',
      name: '',
      email: '',
      phone: '',
      grupo: '',
      expiredDate: undefined,
      active: true,
      createdAt: new Date(),
      usageTime: 0
    };
  }

  private clearMessages(): void {
    setTimeout(() => {
      this.successMessage = '';
      this.errorMessage = '';
    }, 3000);
  }
}
